import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config/api";
import BiChart from "./BiChart";
import DrillDownDialog from "./DrillDownDialog";

const formatValue = (card) => {
  if (card.format === "percent") return `${card.value}%`;
  if (card.format === "days") return `${card.value} d`;
  return card.value;
};

export const BiDashboard = ({ presets = [] }) => {
  const [preset, setPreset] = useState("last_30_days");
  const [kpis, setKpis] = useState(null);
  const [stages, setStages] = useState([]);
  const [trend, setTrend] = useState(null);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [kpiRes, stageRes, trendRes, areaRes] = await Promise.all([
        axios.post(`${API}/bi/kpis?date_preset=${preset}`, {}, { withCredentials: true }),
        axios.post(
          `${API}/bi/aggregate`,
          { collection: "pipeline_cases", group_by: ["current_stage"], metrics: ["record_count"], date_preset: preset },
          { withCredentials: true }
        ),
        axios.post(
          `${API}/bi/time-series`,
          {
            collection: "pipeline_cases",
            metrics: ["record_count"],
            period: "month",
            series_by: "current_pipeline",
            date_preset: preset === "last_7_days" ? "last_90_days" : preset,
          },
          { withCredentials: true }
        ),
        axios.post(
          `${API}/bi/compare`,
          { collection: "pipeline_cases", dimension: "area", metric: "record_count", date_preset: preset, limit: 8 },
          { withCredentials: true }
        ),
      ]);
      setKpis(kpiRes.data);
      setStages(stageRes.data.rows || []);
      setTrend(trendRes.data);
      setAreas(areaRes.data.rows || []);
    } catch (error) {
      console.error("BI dashboard failed", error);
      toast.error(error?.response?.data?.detail || "Could not load the dashboard");
    } finally {
      setLoading(false);
    }
  }, [preset]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6" data-testid="bi-dashboard">
      <div className="flex flex-wrap items-center gap-3">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Period</Label>
        <select
          value={preset}
          onChange={(e) => setPreset(e.target.value)}
          className="h-9 px-2 rounded-md border border-input bg-background text-sm"
          data-testid="dashboard-preset-select"
        >
          {presets.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="kpi-grid">
        {(kpis?.cards || []).map((card) => (
          <Card key={card.id} data-testid={`kpi-${card.id}`}>
            <CardContent className="pt-5">
              <p className="text-xs text-muted-foreground">{card.label}</p>
              <p className="text-2xl font-bold tracking-tight mt-1">{formatValue(card)}</p>
              {card.hint && <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{card.hint}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card data-testid="dashboard-stage-card">
          <CardHeader>
            <CardTitle className="text-base">Where the cases are sitting</CardTitle>
            <CardDescription>Click a bar to see those referrals</CardDescription>
          </CardHeader>
          <CardContent>
            <BiChart
              type="bar"
              columns={["current_stage", "record_count"]}
              rows={stages}
              onSelect={(row) =>
                setDrill({
                  title: `Referrals in ${row.current_stage}`,
                  request: {
                    collection: "pipeline_cases",
                    filters: { current_stage: row.current_stage },
                    date_preset: preset,
                  },
                })
              }
            />
          </CardContent>
        </Card>

        <Card data-testid="dashboard-trend-card">
          <CardHeader>
            <CardTitle className="text-base">Referral trend by pipeline</CardTitle>
            <CardDescription>Monthly volume, SS vs TT</CardDescription>
          </CardHeader>
          <CardContent>
            <BiChart
              type="line"
              columns={["period", ...(trend?.series || [])]}
              rows={trend?.points || []}
              valueKeys={trend?.series || []}
            />
          </CardContent>
        </Card>

        <Card className="xl:col-span-2" data-testid="dashboard-area-card">
          <CardHeader>
            <CardTitle className="text-base">Area vs area</CardTitle>
            <CardDescription>This period against the one before it</CardDescription>
          </CardHeader>
          <CardContent>
            <BiChart
              type="bar"
              columns={["label", "current", "previous"]}
              rows={areas}
              valueKeys={["current", "previous"]}
              onSelect={(row) =>
                setDrill({
                  title: `Referrals in ${row.label}`,
                  request: { collection: "pipeline_cases", filters: { area: row.label }, date_preset: preset },
                })
              }
            />
          </CardContent>
        </Card>
      </div>

      <DrillDownDialog
        open={Boolean(drill)}
        onOpenChange={(open) => !open && setDrill(null)}
        title={drill?.title}
        request={drill?.request}
      />
    </div>
  );
};

export default BiDashboard;
